/**
 * Build-version auto-refresh (v79.21 — Feb 2026).
 *
 * Why this file exists
 * --------------------
 * The Adukoo Android app is a Capacitor wrapper that loads
 * https://adukoo.com via `server.url`. The Android WebView aggressively
 * caches the HTML entry-point ("/") and the /static/js/main.<hash>.js
 * chunk. When we redeploy the web (fixing a bug or shipping a feature),
 * installed apps can hold onto the OLD bundle for hours or days —
 * founder-reported yellow-band bug is one example: the fix was live on
 * adukoo.com but the WebView still rendered the pre-fix bundle.
 *
 * How it works
 * ------------
 * On every full page load, we capture the filename hash of the
 * currently-executing `main.<hash>.js` from the DOM. Then, on a low
 * cadence (every 5 minutes AND every time the app returns to the
 * foreground), we fetch `/asset-manifest.json` (Create React App writes
 * this on every build — zero build-script changes needed) with
 * `cache: 'no-store'` so Cloudflare / WebView cache is bypassed. If the
 * deployed main.js hash differs from the one currently running, a NEW
 * build is live and we hard-reload:
 *
 *   1) Delete every Cache-API storage bucket (`caches.keys()` → delete)
 *   2) Unregister every service worker (defensive; we don't ship one
 *      today but this protects against future SW additions)
 *   3) Replace `window.location` with the same URL + `?_v=<epoch-ms>`
 *      so the WebView treats it as a fresh navigation and refetches
 *      index.html + main.js with the new hash.
 *
 * Cost is ~200 bytes of JSON per 5 min per open tab — negligible.
 *
 * Web-only, safe on native: the flow works identically inside the
 * Android Capacitor WebView because the WebView is a Chromium engine
 * with full Cache-API + fetch support. This is the sanctioned way to
 * push a fresh bundle to installed apps without a Play Store update.
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const MANIFEST_PATH = "/asset-manifest.json";

let currentMainJs = null;
let started = false;
let inflight = false;
let reloadInProgress = false;

/**
 * Snapshot the currently-running main.<hash>.js filename from the DOM.
 * CRA injects <script src="/static/js/main.<hash>.js"> as the last
 * body script, so we scan for it once at boot and cache the value.
 */
function captureCurrentMainJs() {
    try {
        const scripts = document.querySelectorAll('script[src]');
        for (const s of scripts) {
            const src = s.getAttribute("src") || "";
            const m = src.match(/\/static\/js\/main\.[a-f0-9]+\.js/);
            if (m) return m[0];
        }
    } catch (_e) { /* noop */ }
    return null;
}

async function purgeClientCachesAndReload() {
    if (reloadInProgress) return;
    reloadInProgress = true;
    try {
        // 1) Cache API — the storage bucket CRA / Workbox would use.
        if (typeof caches !== "undefined" && caches?.keys) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
        }
    } catch (_e) { /* silent — reload will still happen */ }
    try {
        // 2) Service workers — defensive.
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
        }
    } catch (_e) { /* silent */ }
    // 3) Hard reload with a cache-busting query param so the WebView
    //    refetches index.html + main.js from network (Cloudflare + any
    //    intermediate CDN respect the query string as a cache key).
    try {
        const url = new URL(window.location.href);
        url.searchParams.set("_v", String(Date.now()));
        window.location.replace(url.toString());
    } catch (_e) {
        // Absolute fallback if URL parsing dies for any reason.
        window.location.href = window.location.href;
    }
}

async function checkForNewBuild() {
    if (inflight || reloadInProgress) return;
    if (!currentMainJs) return;
    inflight = true;
    try {
        const r = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, {
            cache: "no-store",
            credentials: "same-origin",
        });
        if (!r.ok) return;
        const manifest = await r.json();
        const deployedMainJs = manifest?.files?.["main.js"];
        if (!deployedMainJs) return;
        // Normalise: manifest returns "/static/js/main.<hash>.js", DOM
        // scan returns the same. Compare filename portions to be safe.
        const deployedName = deployedMainJs.split("/").pop();
        const currentName = currentMainJs.split("/").pop();
        if (deployedName && currentName && deployedName !== currentName) {
            // A new build is live. Purge + reload.
            console.info(`[buildVersionCheck] New build detected: ${currentName} → ${deployedName}. Reloading…`);
            await purgeClientCachesAndReload();
        }
    } catch (_e) {
        /* silent — offline / rate-limited / CORS blip. Retry on next tick. */
    } finally {
        inflight = false;
    }
}

/**
 * Wire up the checker. Safe to call multiple times — internally
 * idempotent via the `started` guard.
 */
export function installBuildVersionCheck() {
    if (started) return;
    if (typeof window === "undefined") return;
    started = true;
    currentMainJs = captureCurrentMainJs();
    // If we couldn't identify the current bundle, do nothing — we'd
    // false-positive on every check.
    if (!currentMainJs) return;
    // Fire once ~10 s after boot so we don't compete with the first
    // paint / hydrate work, then every CHECK_INTERVAL_MS.
    setTimeout(checkForNewBuild, 10_000);
    setInterval(checkForNewBuild, CHECK_INTERVAL_MS);
    // Also check whenever the tab / app returns to the foreground —
    // this catches Android users who tab away for a few minutes and
    // come back after we've pushed a fix.
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
            checkForNewBuild();
        }
    });
    // pageshow fires on back/forward navigation on some Android
    // WebView versions where visibilitychange doesn't.
    window.addEventListener("pageshow", () => {
        checkForNewBuild();
    });
}
